import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useTabs } from '@/features/tabs';

import {
  type SignInConflict,
  useCloseOtherSignInMutation,
  useSignInSessionQuery,
} from './sign-in-session.query';

export const SignInConflictCard = ({
  conflict,
  threadId,
}: {
  conflict: SignInConflict;
  threadId: string;
}) => {
  const { openTab } = useTabs();
  const closeOther = useCloseOtherSignInMutation(threadId);
  const { data: session } = useSignInSessionQuery(conflict.sign_in_session_id);
  const continuationPending =
    session?.continuations_pending?.includes(threadId) === true;
  // The session poll is the source of truth: a failed close must not keep the card open
  // once the other sign-in has ended on its own
  const sessionGone = session === null || session?.profile_released === true;
  const closed = !continuationPending && (closeOther.isSuccess || sessionGone);

  return (
    <YStack className="w-full gap-3 rounded-xl border border-border bg-surface p-4">
      {closed ? (
        <p role="status">
          {closeOther.isSuccess
            ? 'Other sign-in ended. Continuing…'
            : 'Other sign-in ended.'}
        </p>
      ) : (
        <>
          <p>
            {continuationPending ? (
              'Sign-in ended. Continue this conversation.'
            ) : (
              <>
                A sign-in for {new URL(conflict.origin).host} is open in another
                {conflict.source === 'messaging_channel'
                  ? ' messaging conversation.'
                  : ' chat.'}{' '}
                Closing it cancels that sign-in.
              </>
            )}
          </p>
          <XStack className="flex-wrap gap-2">
            {!continuationPending && conflict.source !== 'messaging_channel' ? (
              <Button
                variant="secondary"
                size="sm"
                textSize="base"
                onClick={() =>
                  openTab(
                    { path: `/chats/${conflict.chat_id}` },
                    { reuseExisting: true }
                  )
                }
              >
                Open other chat
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              textSize="base"
              isLoading={closeOther.isPending}
              loadingText="Closing…"
              onClick={() => closeOther.mutate(conflict)}
            >
              {continuationPending ? 'Continue chat' : 'Close other sign-in'}
            </Button>
          </XStack>
          {closeOther.isError && !continuationPending ? (
            <p role="alert" className="text-sm text-foreground-muted">
              Could not close sign-in or continue this chat. Try again.
            </p>
          ) : null}
        </>
      )}
    </YStack>
  );
};
